import React, { useEffect, useState } from "react";
import { Form, Row, Col, Button } from "react-bootstrap";
import "../styles/TaskFilter.css";
import { useDispatch, useSelector } from "react-redux";
import { StoreState } from "../redux/reducers";
import { fetchProjects } from "../redux/actions/projectAction";
import { fetchModules } from "../redux/actions/moduleAction";
import { fetchSubModules } from "../redux/actions/subModuleAction";
import { fetchPages } from "../redux/actions/pageAction";
import { fetchSprints } from "../redux/actions/SprintAction";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const initialFilterState = {
  project: "",
  status: "",
  assignedDev: "",
  assignedQa: "",
  assignedBa: "",
  module: "",
  submodule: "",
  sprint: "",
  page: "",
};

interface FilterProps {
  filters: typeof initialFilterState;
  onFilterChange: (field: string, value: string) => void;
  onFind: () => void;
  onReset: () => void;
  statusOptions: string[];
  devOptions: string[];
  qaOptions: string[];
  baOptions: string[];
  moduleOptions: string[];
  submoduleOptions: string[];
  pageOptions: string[];
  sprintOptions: string[];
}

const TaskFilter: React.FC<FilterProps> = ({
  filters,
  onFilterChange,
  onReset,
  onFind,
  statusOptions,
  devOptions,
  qaOptions,
  baOptions,
}) => {
  const dispatch = useDispatch<any>();
  const projects = useSelector((state: StoreState) => state.project);
  const moduleOptions = useSelector((state: StoreState) => state.module);
  const submoduleOptions = useSelector((state: StoreState) => state.subModule);
  const pageOptions = useSelector((state: StoreState) => state.page);
  const sprintOptions = useSelector((state: StoreState) => state.sprint);

  const [lastAppliedFilters, setLastAppliedFilters] =
    useState(initialFilterState);

  const handleFindClick = () => {
    // --- FIX: Allow finding with no filters (shows all tasks) ---
    localStorage.setItem("taskFilters", JSON.stringify(filters));
    setLastAppliedFilters(filters);
    onFind();
  };

  const filteredModules = moduleOptions.filter(
    (module) =>
      filters.project === "" ||
      module.projectId?.toString() === filters.project,
  );

  const filteredSubmodules = submoduleOptions.filter(
    (submodule) =>
      filters.module === "" ||
      submodule.moduleId?.toString() === filters.module,
  );

  const filteredPages = pageOptions.filter(
    (page) =>
      filters.submodule === "" ||
      page.subModuleId?.toString() === filters.submodule,
  );

  const handleDropdownChange = (name: string, value: string) => {
    if (name === "project") {
      onFilterChange("module", "");
      onFilterChange("submodule", "");
      onFilterChange("page", "");
    } else if (name === "module") {
      onFilterChange("submodule", "");
      onFilterChange("page", "");
    } else if (name === "submodule") {
      onFilterChange("page", "");
    }
    onFilterChange(name, value);
  };

  const renderDropdown = (
    label: string,
    name: string,
    value: string,
    options: { id: string; name: string }[] | string[],
  ) => (
    <Form.Group
      as={Col}
      xs={6}
      sm={4}
      md={3}
      lg={2}
      className="task-filter-field"
      controlId={name}
    >
      <div className="dropdown-label">{label}</div>
      <Form.Select
        value={value}
        onChange={(e) => handleDropdownChange(name, e.target.value)}
        className="custom-select"
      >
        <option value="">All</option>
        {options.map((opt, idx) =>
          typeof opt === "string" ? (
            <option key={idx} value={opt}>
              {opt}
            </option>
          ) : (
            <option key={idx} value={opt.id}>
              {opt.name}
            </option>
          ),
        )}
      </Form.Select>
    </Form.Group>
  );

  useEffect(() => {
    dispatch(fetchProjects());
    dispatch(fetchModules());
    dispatch(fetchSubModules());
    dispatch(fetchPages());
    dispatch(fetchSprints());
  }, [dispatch]);

  return (
    <Form className="task-filter-form">
      <ToastContainer />
      <Row className="g-2 align-items-end">
        {renderDropdown(
          "Project",
          "project",
          filters.project,
          projects.map((p) => ({ id: p.id.toString(), name: p.name })),
        )}
        {renderDropdown(
          "Module",
          "module",
          filters.module,
          filteredModules.map((m) => ({ id: m.id.toString(), name: m.name })),
        )}
        {renderDropdown(
          "Submodule",
          "submodule",
          filters.submodule,
          filteredSubmodules.map((sm) => ({
            id: sm.id.toString(),
            name: sm.name,
          })),
        )}
        {renderDropdown(
          "Page",
          "page",
          filters.page,
          filteredPages.map((p) => ({ id: p.id.toString(), name: p.name })),
        )}
        {renderDropdown(
          "Sprint",
          "sprint",
          filters.sprint,
          sprintOptions.map((s) => ({ id: s.id.toString(), name: s.sprint })),
        )}
        {renderDropdown("Status", "status", filters.status, statusOptions)}
        {renderDropdown(
          "BA Assigned",
          "assignedBa",
          filters.assignedBa,
          baOptions,
        )}
        {renderDropdown(
          "Dev Assigned",
          "assignedDev",
          filters.assignedDev,
          devOptions,
        )}
        {renderDropdown(
          "QA Assigned",
          "assignedQa",
          filters.assignedQa,
          qaOptions,
        )}

        <Col xs={12} className="d-flex justify-content-end">
          <Button variant="primary" onClick={handleFindClick} className="m-1">
            Find
          </Button>
          <Button variant="secondary" onClick={onReset} className="m-1">
            Reset
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default TaskFilter;